/**
 * Web Push hook & helper component.
 *
 * Usage:
 *   const { supported, permission, subscribed, subscribe, unsubscribe, sendTest } = usePush();
 *   <PushToggle />
 *
 * iPhone Safari requires the user to first "Add to Home Screen" (PWA install)
 * before push permission can be granted. On Android Chrome it works in any tab.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

function browserSupportsPush() {
    return (
        typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

function isIosStandalonePwa() {
    // iOS PWAs report this; Safari tabs do not.
    return (
        window.navigator.standalone === true ||
        window.matchMedia?.("(display-mode: standalone)").matches
    );
}

function isIos() {
    const ua = navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua);
}

export function usePush() {
    const { user } = useAuth();
    const [supported] = useState(browserSupportsPush());
    const [permission, setPermission] = useState(
        supported ? Notification.permission : "default"
    );
    const [subscribed, setSubscribed] = useState(false);
    const [registration, setRegistration] = useState(null);
    const [busy, setBusy] = useState(false);

    // Register service worker once on mount (regardless of permission)
    useEffect(() => {
        if (!supported) return;
        navigator.serviceWorker
            .register("/sw.js")
            .then(async (reg) => {
                setRegistration(reg);
                const sub = await reg.pushManager.getSubscription();
                setSubscribed(!!sub);
            })
            .catch((e) => console.warn("SW register failed", e));
    }, [supported]);

    const subscribe = useCallback(async () => {
        if (!supported || !registration || !user) return;
        setBusy(true);
        try {
            // 1. Request notification permission (gesture-driven)
            let perm = Notification.permission;
            if (perm === "default") {
                perm = await Notification.requestPermission();
                setPermission(perm);
            }
            if (perm !== "granted") {
                throw new Error("Permesso notifiche negato");
            }

            // 2. Get VAPID public key
            const { data } = await api.get("/push/vapid-public-key");
            const appServerKey = urlBase64ToUint8Array(data.public_key);

            // 3. Subscribe via PushManager
            let sub = await registration.pushManager.getSubscription();
            if (!sub) {
                sub = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: appServerKey,
                });
            }

            // 4. Send to backend
            const json = sub.toJSON();
            await api.post("/push/subscribe", {
                endpoint: json.endpoint,
                keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
                ua: navigator.userAgent,
            });
            setSubscribed(true);
            return true;
        } finally {
            setBusy(false);
        }
    }, [supported, registration, user]);

    const unsubscribe = useCallback(async () => {
        if (!registration) return;
        setBusy(true);
        try {
            const sub = await registration.pushManager.getSubscription();
            if (sub) {
                await api
                    .post("/push/unsubscribe", { endpoint: sub.endpoint })
                    .catch(() => {});
                await sub.unsubscribe();
            }
            setSubscribed(false);
        } finally {
            setBusy(false);
        }
    }, [registration]);

    const sendTest = useCallback(async () => {
        const { data } = await api.post("/push/test");
        return data; // { sent: <numero dispositivi raggiunti> }
    }, []);

    return {
        supported,
        permission,
        subscribed,
        busy,
        subscribe,
        unsubscribe,
        sendTest,
        isIos: isIos(),
        isIosStandalone: isIosStandalonePwa(),
    };
}

/**
 * Toggle button + onboarding hints.
 * Hidden until user is logged in (no point subscribing anonymous).
 */
export function PushToggle({ compact = false }) {
    const { user } = useAuth();
    const push = usePush();
    const [msg, setMsg] = useState(null);

    if (!user) return null;
    if (!push.supported) {
        return (
            <div className="op-mono text-xs text-t8f95a3" data-testid="push-unsupported">
                Browser non supporta le push notifications.
            </div>
        );
    }

    // iPhone Safari (NOT installed as PWA) → can't subscribe yet
    if (push.isIos && !push.isIosStandalone) {
        if (compact) {
            return (
                <div className="op-mono text-xs text-tff7a1a" data-testid="push-ios-hint">
                    📲 Installa l'app sulla Home per ricevere notifiche
                </div>
            );
        }
        return (
            <div className="op-card p-4" data-testid="push-ios-hint-card">
                <div className="op-label mb-2" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// NOTIFICHE iOS</div>
                <p className="text-sm text-tc9ccd3 leading-relaxed">
                    Su iPhone le notifiche funzionano solo dopo aver installato l'app sulla Home:
                </p>
                <ol className="text-sm text-tc9ccd3 mt-2 ml-5 list-decimal space-y-1">
                    <li>Tocca il bottone <span className="op-mono">Condividi</span> di Safari</li>
                    <li>Scegli <span className="op-mono">Aggiungi a Home</span></li>
                    <li>Apri "Original PES" dalla Home e torna qui</li>
                </ol>
            </div>
        );
    }

    const onSubscribe = async () => {
        setMsg(null);
        try {
            await push.subscribe();
            setMsg({ kind: "ok", text: "Notifiche attivate! Inviamo una notifica di prova…" });
            // small test after subscribe
            setTimeout(() => push.sendTest(), 500);
        } catch (e) {
            setMsg({ kind: "err", text: e.message || "Errore" });
        }
    };

    const onUnsubscribe = async () => {
        try {
            await push.unsubscribe();
            setMsg({ kind: "ok", text: "Notifiche disattivate" });
        } catch (e) {
            setMsg({ kind: "err", text: e.message || "Errore" });
        }
    };

    const onTest = async () => {
        setMsg(null);
        try {
            const r = await push.sendTest();
            const sent = r?.sent ?? 0;
            setMsg(sent > 0
                ? { kind: "ok", text: `Notifica di prova inviata a ${sent} dispositivo/i ✅` }
                : { kind: "err", text: "Nessun dispositivo raggiunto: prova a disattivare e riattivare le notifiche." });
        } catch (e) {
            setMsg({ kind: "err", text: "Invio test fallito: " + (e.response?.data?.detail || e.message) });
        }
    };

    if (push.subscribed) {
        return (
            <div className="flex items-center gap-2 flex-wrap" data-testid="push-subscribed">
                <span className="op-mono text-xs text-t2bb6ff">🔔 Notifiche attive</span>
                <button
                    className="op-btn op-btn-ghost text-xs"
                    onClick={onTest}
                    disabled={push.busy}
                    data-testid="push-test-btn"
                >
                    Invia test
                </button>
                <button
                    className="op-btn op-btn-ghost text-xs"
                    onClick={onUnsubscribe}
                    disabled={push.busy}
                    data-testid="push-unsubscribe-btn"
                >
                    Disattiva
                </button>
                {msg && (
                    <span className={`op-mono text-xs ${msg.kind === "ok" ? "text-t2bb6ff" : "text-tff4d4d"}`}>
                        {msg.text}
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2 flex-wrap" data-testid="push-not-subscribed">
            <button
                className="op-btn op-btn-primary"
                onClick={onSubscribe}
                disabled={push.busy || push.permission === "denied"}
                data-testid="push-subscribe-btn"
            >
                {push.permission === "denied" ? "🚫 Notifiche bloccate" : "🔔 Attiva notifiche"}
            </button>
            {msg && (
                <span className={`op-mono text-xs ${msg.kind === "ok" ? "text-t2bb6ff" : "text-tff4d4d"}`}>
                    {msg.text}
                </span>
            )}
        </div>
    );
}
